import React from 'react';
import PropTypes from 'prop-types';
import { NamespaceConsumer, NamespaceShape, withNamespace } from '@ux/namespace-component';

/**
* Display popover content when click on the control component
*
* @displayName PopOver (Web)
* @class PopOver
* @public
*/
export class PopOver extends NamespaceConsumer {
  constructor(props) {
    super(props);
    this.state = {
      ...this.state,
      open: props.open,
      alignment: props.popoverAlignment
    };
    this.toggle = this.toggle.bind(this);
    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
  }

  /**
   * Open the popover by setting open to true
   *
   * @private
   */
  open() {
    this.setState({ open: true }, () => this.props.onToggle(true));
  }

  /**
   * Close the popover by setting open to false
   *
   * @private
   */
  close() {
    this.setState({ open: false }, () => this.props.onToggle(false));
  }

  /**
   * Toggle the popover based on current state of open
   *
   * @private
   */
  toggle() {
    const next = this.state.open;
    next ?
      this.close() :
      this.open();
  }

  /**
   * Hide the dropdown if clicks are outside the popover itself.
   */
  componentDidMount() {
    this.outside('click', this.close, this.contentWrapper);
    this.outside('focusin', this.close, this.contentWrapper);
    const alignment = this.getAlignment();
    this.setState({ ...this.state, alignment });
  }

  /**
   * Remove the attached click event listener.
   */
  componentWillUnmount() {
    this.off('click', this.close);
    this.off('focusin', this.close);
  }

  /**
   * Check if the open prop has changed.
   * If so, update state of open.
   *
   * @param {Object} prevProps - previous instance of props
   * @param {Object} prevState - previous instance of state
   */
  componentDidUpdate(prevProps, prevState) {
    const { open } = this.state;
    const { popoverAlignment } = this.props;

    if (popoverAlignment && popoverAlignment !== prevProps.popoverAlignment) {
      this.setState({
        ...this.state,
        alignment: popoverAlignment
      });
    } else if (open && open !== prevState.open) {
      const alignment = this.getAlignment();
      if (alignment !== prevState.alignment) {
        this.setState({ ...this.state, alignment });
      }
    }

    if (prevProps.open !== this.props.open && this.state.open !== this.props.open) {
      this.setState({ open: this.props.open });
    }
  }

  /**
   * Fetches all element bounds to necessary calculations of display
   * @private
   * @returns {object} element bounds
   */
  getElementBounds() {
    // Viewport Bounds
    const viewportBounds = {
      x: window.pageXOffset,
      y: window.pageYOffset,
      width: window.innerWidth,
      height: window.innerHeight
    };

    // Wrapper Div for Popover
    const wrapperDivBounds = this.contentWrapper && this.contentWrapper.getBoundingClientRect();

    // Popover Element
    const menuBounds = this.popoverElement &&
                       this.popoverElement.getBoundingClientRect();

    return { viewportBounds, wrapperDivBounds, menuBounds };
  }

  /**
   * Compute horizontal spacing for menu and determine whether to show aligned left or right to the popover.
   *
   * @returns {String|Boolean} The direction of alignment. False if neither left nor right.
   * @private
   */
  getAlignment() {
    let alignment = '';
    const { wrapperDivBounds, menuBounds, viewportBounds } = this.getElementBounds();
    if (wrapperDivBounds && menuBounds) {
      const right = wrapperDivBounds.left + viewportBounds.x - window.document.documentElement.clientLeft + menuBounds.width;
      // The element needs to be right aligned if it's too close or outside the bounds of the viewport
      // and if it is small enough to fit inside a viewport, if it's too big we'll default to left
      if (viewportBounds.width - right < 10 && menuBounds.width < viewportBounds.width) {
        alignment = 'right';
      } else {
        // otherwise align left
        alignment = 'left';
      }
    }

    return alignment;
  }

  /**
   * Render the control component within a button and the popover content children.
   *
   * @returns {ReactElement} PopOver element.
   * @private
   */
  render() {
    const { children, className, control, eid } = this.props;

    const { open, alignment } = this.state;

    // check that we are in viewport bounds && bidi?
    const alignClass = (alignment === 'left') ? 'popover-align-left' : 'popover-align-right';

    // Don't namespace the incoming `props.className` -- the parent component should do so
    const wrapperClassNames = this.classNames(this.namespace(
      this.classNames('popover',
        { open })
    ), alignClass, className);

    return (
      <div className={ wrapperClassNames } data-eid={ eid } ref={ r => { this.contentWrapper = r; } }>
        <button onClick={ this.toggle } onKeyDown={ this.toggle } tabIndex='0' className={ this.namespace('popover-button') }>
          { control }
        </button>
        <div className={ this.namespace('content') } ref={ r => { this.popoverElement = r; } }>
          { children }
        </div>
      </div>
    );
  }
}

PopOver.propTypes = {
  ...NamespaceShape,
  /** Customized class name */
  className: PropTypes.string,
  /** Control component for button render  */
  control: PropTypes.element,
  /** Toggle handler for when popover is opened or closed */
  onToggle: PropTypes.func,
  /** eid to be used for the traffic tracking */
  eid: PropTypes.string,
  /** Allows consumer to open or close the popover */
  open: PropTypes.bool,
  /** Aligns popover on left or right */
  popoverAlignment: PropTypes.oneOf(['left', 'right'])
};

PopOver.defaultProps = {
  onToggle: () => { },
  open: false,
  popoverAlignment: 'left'
};

export default withNamespace(PopOver);
